import logger from 'ws-scripts/modules/logger';
import { PAYMENT_TYPE_CONFIG, DEAL_TYPE_CONFIG } from '../constants';
import { sortByDisplayIndex, markPositions } from '../utils/transformation';

export const processLegacyPayment = (
	udsPayments,
	{ enableIncentives, listingType, cachePreloadEnabled }
) => {
	const rateKey = udsPayments?.rate ? 'rate' : 'rates';

	if (
		!udsPayments[rateKey] ||
		typeof udsPayments[rateKey] !== 'object'
	) {
		logger.warn(
			'WS-TRACK-MY-OFFER: Payment data missing rate/rates structure',
			{
				udsPayments,
				rateKey,
				hasRate: !!udsPayments[rateKey]
			}
		);
		return null;
	}

	const creditRatingKey = Object.keys(udsPayments[rateKey])[0];
	if (!creditRatingKey) {
		logger.warn('WS-TRACK-MY-OFFER: No credit rating key found', {
			rateKey,
			rateData: udsPayments[rateKey]
		});
		return null;
	}

	const paymentTypes = udsPayments[rateKey][creditRatingKey];
	if (!paymentTypes || typeof paymentTypes !== 'object') {
		logger.warn('WS-TRACK-MY-OFFER: No payment types found', {
			creditRatingKey,
			paymentTypes
		});
		return null;
	}

	const supportedDealTypes = udsPayments.supportedDealTypes || [];
	logger.info('WS-TRACK-MY-OFFER: Supported deal types', {
		supportedDealTypes
	});

	const actualPaymentKeys = Object.keys(paymentTypes);

	const paymentKeys =
		supportedDealTypes.length > 0
			? supportedDealTypes
					.map((dealType) => {
						if (
							dealType === 'finance' &&
							actualPaymentKeys.includes('nationalOffers')
						) {
							return 'nationalOffers';
						}
						return DEAL_TYPE_CONFIG[dealType];
					})
					.filter(Boolean)
			: actualPaymentKeys;

	const formattedPayments = paymentKeys
		.map((key) => {
			const config =
				PAYMENT_TYPE_CONFIG[key] ||
				PAYMENT_TYPE_CONFIG.paymentOffers;
			const { label: paymentLabel, displayIndex } = config;
			const errorKey = `is${paymentLabel}ErrorState`;

			if (udsPayments[errorKey]) {
				logger.warn(
					`WS-TRACK-MY-OFFER: Skipping payment type with error ${key}`
				);
				return null;
			}

			if (
				!paymentTypes[key] ||
				typeof paymentTypes[key] !== 'object'
			) {
				logger.warn(
					`WS-TRACK-MY-OFFER: No data for payment type ${key}`
				);
				return null;
			}

			const paymentTermKeys = Object.keys(paymentTypes[key]);
			if (paymentTermKeys.length === 0) {
				logger.warn(
					`WS-TRACK-MY-OFFER: No payment terms for ${key}`
				);
				return null;
			}

			const paymentTermKey = paymentTermKeys[0];
			const paymentTermData = paymentTypes[key][paymentTermKey];

			if (
				!Array.isArray(paymentTermData) ||
				paymentTermData.length === 0
			) {
				logger.warn(
					`WS-TRACK-MY-OFFER: No payment data array for ${key}/${paymentTermKey}`
				);
				return null;
			}

			const paymentTypeData = paymentTermData[0];
			const payment = !udsPayments[errorKey]
				? (key === 'cashOffers' && cachePreloadEnabled
						? paymentTypeData?.cashAmount
						: paymentTypeData?.payment) ||
				  paymentTypeData?.pricingDetails?.salePrice
				: 0;

			if (key === 'cashOffers') {
				logger.info(
					'WS-TRACK-MY-OFFER: Cash offer payment calculation',
					{
						key,
						cashAmount: paymentTypeData?.cashAmount,
						payment: paymentTypeData?.payment,
						salePrice:
							paymentTypeData?.pricingDetails?.salePrice,
						finalPayment: payment
					}
				);
			}

			const incentiveIds = paymentTypeData?.incentiveIds;
			const paymentTerm =
				paymentTermKey !== '-1' ? paymentTermKey : null;
			const downPayment = paymentTypeData?.downPayment;
			const aprListingType =
				listingType === 'new' ? listingType : 'used';
			const apr = paymentTypeData?.[aprListingType];

			return {
				payment: Math.floor(payment),
				paymentTerm,
				paymentLabel,
				hasSpecial:
					enableIncentives === 'true' &&
					incentiveIds !== undefined &&
					incentiveIds.length !== 0,
				incentiveIds,
				key: `paymentOption_${key}`,
				displayIndex,
				downPayment,
				apr,
				annualMiles: paymentTypeData?.annualMiles,
				hasTaxesAndFees: !!(
					paymentTypeData.taxesAndFees ||
					paymentTypeData.taxesFees
				),
				isSelected:
					udsPayments?.dealType ===
					paymentLabel.toLowerCase()
			};
		})
		.filter((payment) => payment !== null)
		.sort(sortByDisplayIndex);

	if (formattedPayments.length > 0) {
		return {
			payments: markPositions(formattedPayments),
			hasError: false
		};
	}

	logger.warn('WS-TRACK-MY-OFFER: No valid payment data found');
	return { payments: [], hasError: true };
};
